// hooks/useSharedThumbnails.js
import { ref, reactive } from 'vue';
import { useProjectStore } from '@/stores/projects';
import { cacheKeys, StorageManager } from '@/utils/cache';
import { getFullUrl } from '@/api';

// No async operations during route changes
window.isRouteChanging = false;

// Global cache
if (!window.thumbnailCache) {
    window.thumbnailCache = {
        visibleImages: {},
        loadingStatus: {},
        projectsBeingLoaded: new Set()
    };
}

export function useSharedThumbnails() {
    const projectStore = useProjectStore();
    const projectLoadingStatus = ref(window.thumbnailCache.loadingStatus);
    const projectVisibleImages = ref(window.thumbnailCache.visibleImages);
    const loadingQueue = ref([]);
    const isCurrentlyLoading = ref(false);
    const projectsBeingLoaded = ref(window.thumbnailCache.projectsBeingLoaded);

    // Pause loading during route changes
    const pauseLoadingForRouteChange = () => {
        window.isRouteChanging = true;
        setTimeout(() => {
            window.isRouteChanging = false;
        }, 500);
    };

    // Load cached thumbnails
    const loadCachedThumbnails = () => {
        try {
            const cachedThumbnails = StorageManager.get(cacheKeys.THUMBNAILS);
            if (cachedThumbnails) {
                if (cachedThumbnails.visibleImages) {
                    window.thumbnailCache.visibleImages = {
                        ...window.thumbnailCache.visibleImages,
                        ...cachedThumbnails.visibleImages
                    };
                }
                if (cachedThumbnails.loadingStatus) {
                    window.thumbnailCache.loadingStatus = {
                        ...window.thumbnailCache.loadingStatus,
                        ...cachedThumbnails.loadingStatus
                    };
                }

                projectVisibleImages.value = window.thumbnailCache.visibleImages;
                projectLoadingStatus.value = window.thumbnailCache.loadingStatus;
            }
        } catch (e) {
            console.warn('Error loading cached thumbnails:', e);
        }
    };

    // Call immediately
    loadCachedThumbnails();

    // Check if project is ready
    const isProjectFullyLoaded = (projectId) => {
        if (!projectId) return false;
        if (projectsBeingLoaded.value.has(projectId)) return false;
        return (projectVisibleImages.value[projectId]?.length > 0);
    };

    // Check if loading spinner should be visible
    const isLoadingSpinnerVisible = (projectId) => {
        if (!projectId) return false;
        return projectStore.isProjectLoading(projectId) || projectsBeingLoaded.value.has(projectId);
    };

    // Process loading queue
    const processLoadingQueue = async () => {
        if (window.isRouteChanging) return;
        if (isCurrentlyLoading.value || loadingQueue.value.length === 0) return;

        isCurrentlyLoading.value = true;

        try {
            const projectId = loadingQueue.value[0];
            const project = projectStore.projects.find(p => p.id === projectId);

            if (project) {
                projectsBeingLoaded.value.add(project.id);
                window.thumbnailCache.projectsBeingLoaded = projectsBeingLoaded.value;

                // Set visible images immediately for better performance
                const allImages = getProjectAllImages(project);
                if (allImages.length > 0) {
                    projectVisibleImages.value[project.id] = allImages;
                    window.thumbnailCache.visibleImages = { ...projectVisibleImages.value };
                }

                loadAllThumbnails(project);
                projectStore.markProjectLoaded(project.id);

                projectsBeingLoaded.value.delete(project.id);
                window.thumbnailCache.projectsBeingLoaded = projectsBeingLoaded.value;
            }

            loadingQueue.value.shift();
            isCurrentlyLoading.value = false;

            if (!window.isRouteChanging) {
                setTimeout(() => processLoadingQueue(), 0);
            }
        } catch (error) {
            console.error('Error processing loading queue:', error);

            if (loadingQueue.value[0]) {
                projectsBeingLoaded.value.delete(loadingQueue.value[0]);
                window.thumbnailCache.projectsBeingLoaded = projectsBeingLoaded.value;
            }

            loadingQueue.value.shift();
            isCurrentlyLoading.value = false;

            if (!window.isRouteChanging) {
                setTimeout(() => processLoadingQueue(), 0);
            }
        }
    };

    // Save thumbnails to localStorage
    const saveThumbnailsToLocalStorage = () => {
        if (window.isRouteChanging) return;

        try {
            const sortedProjects = projectStore.sortedProjects;
            const firstFiveProjects = sortedProjects.slice(0, 5).map(p => p.id);

            const thumbnailsToSave = {
                visibleImages: {},
                loadingStatus: {}
            };

            firstFiveProjects.forEach(projectId => {
                if (projectVisibleImages.value[projectId]) {
                    thumbnailsToSave.visibleImages[projectId] = projectVisibleImages.value[projectId];
                }
                if (projectLoadingStatus.value[projectId]) {
                    thumbnailsToSave.loadingStatus[projectId] = projectLoadingStatus.value[projectId];
                }
            });

            StorageManager.set(cacheKeys.THUMBNAILS, thumbnailsToSave);
        } catch (error) {
            console.warn('Error saving thumbnails:', error);
        }
    };

    // Get visible images for a project
    const getProjectVisibleImages = (projectId) => {
        return projectVisibleImages.value[projectId] || [];
    };

    // Get all images for a project
    const getProjectAllImages = (project) => {
        if (!project?.attributes?.images?.data) return [];
        return project.attributes.images.data;
    };

    // Load thumbnails - non-blocking implementation
    const loadAllThumbnails = async (project) => {
        if (!project || !project.id) return;

        // Check if we already have loaded this project's thumbnails
        if (projectVisibleImages.value[project.id]?.length > 0) {
            return;
        }

        if (!projectLoadingStatus.value[project.id]) {
            projectLoadingStatus.value[project.id] = {
                isLoading: true,
                currentIndex: 0,
                loadedImageIds: new Set(),
                loadedMediumIds: new Set() // Track medium format separately
            };
        }

        if (!projectVisibleImages.value[project.id]) {
            projectVisibleImages.value[project.id] = [];
        }

        const status = projectLoadingStatus.value[project.id];

        const allImages = getProjectAllImages(project);

        if (!allImages.length) {
            status.isLoading = false;
            return;
        }

        try {
            // Preload both thumbnail and medium formats for all images
            const loadPromises = allImages.map(image => {
                const promises = [];

                // Only load thumbnail if not already loaded
                if (!status.loadedImageIds.has(image.id)) {
                    status.loadedImageIds.add(image.id);

                    promises.push(new Promise(resolve => {
                        const img = new Image();
                        if (image.attributes.formats?.thumbnail) {
                            img.src = getFullUrl(image.attributes.formats.thumbnail.url);
                        } else {
                            img.src = getFullUrl(image.attributes.url);
                        }
                        img.onload = () => resolve();
                        img.onerror = () => resolve();
                    }));
                }

                // Only load medium if not already loaded
                if (!status.loadedMediumIds.has(image.id)) {
                    status.loadedMediumIds.add(image.id);

                    // Only attempt to load medium if it exists
                    if (image.attributes.formats?.medium) {
                        promises.push(new Promise(resolve => {
                            const imgMedium = new Image();
                            imgMedium.src = getFullUrl(image.attributes.formats.medium.url);
                            imgMedium.onload = () => resolve();
                            imgMedium.onerror = () => resolve();
                        }));
                    }
                }

                return Promise.all(promises);
            });

            await Promise.all(loadPromises);

            projectVisibleImages.value[project.id] = allImages;
            status.isLoading = false;
            status.currentIndex = allImages.length;

            // Update the global cache
            window.thumbnailCache.visibleImages = { ...projectVisibleImages.value };
            window.thumbnailCache.loadingStatus = { ...projectLoadingStatus.value };
        } catch (error) {
            console.error('Error loading images:', error);
            status.isLoading = false;
        }
    };

    // Get image URL
    const getImageUrl = (project, image, highRes = false) => {
        if (!image) return '';
        const formats = image.attributes?.formats;
        if (formats) {
            if (highRes) {
                if (formats.large) return formats.large.url;
                if (formats.medium) return formats.medium.url;
            } else {
                if (formats.thumbnail) return formats.thumbnail.url;
                if (formats.small) return formats.small.url;
            }
        }
        return image.attributes?.url || '';
    };

    // Queue a project for loading
    const queueProjectForLoading = (project) => {
        if (window.isRouteChanging) return;
        if (!project || !project.id) return;

        // Already loaded
        if (projectVisibleImages.value[project.id]?.length > 0) return;

        if (!loadingQueue.value.includes(project.id) && !projectsBeingLoaded.value.has(project.id)) {
            loadingQueue.value.push(project.id);

            if (!isCurrentlyLoading.value) {
                processLoadingQueue();
            }
        }
    };

    // Load images for visible projects
    const loadImagesForVisibleProjects = () => {
        if (window.isRouteChanging) return;
        if (!projectStore.visibleProjects?.length) return;

        loadingQueue.value = [];

        projectStore.visibleProjects.forEach(project => {
            if (project) {
                queueProjectForLoading(project);
            }
        });
    };

    return {
        projectLoadingStatus,
        projectVisibleImages,
        loadAllThumbnails,
        getProjectVisibleImages,
        getProjectAllImages,
        isProjectFullyLoaded,
        isLoadingSpinnerVisible,
        queueProjectForLoading,
        loadImagesForVisibleProjects,
        getImageUrl,
        saveThumbnailsToLocalStorage,
        pauseLoadingForRouteChange
    };
}